import React, { useState } from "react";
import axios from "axios";

const EVENT_TYPES = ["wedding", "party", "corporate", "fundraiser"];

const DISCOUNT_INFO = [
  { min: 500, pct: 5 },
  { min: 1000, pct: 8 },
  { min: 2000, pct: 10 },
  { min: 5000, pct: 12 },
];

export default function EventInquiryPage() {
  const [form, setForm] = useState({
    event_type: "wedding",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    event_date: "",
    event_location: "",
    guest_count: 50,
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const updateField = (key: string, value: string | number) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const canSubmit = form.customer_name.trim() && form.customer_phone.trim() && form.event_date;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      await axios.post("/api/events/orders", {
        ...form,
        items: [],
      });
      setSubmitted(true);
    } catch {
      setError("Failed to submit inquiry. Please call us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen" style={{ background: "var(--rbw-bg)" }}>
        <header style={{ background: "var(--rbw-navy)" }}>
          <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
            <img src="/images/logo-secondary.jpg" alt="Rieker Bottle Works" className="h-10 rounded" />
            <h1 className="text-xl font-bold" style={{ color: "#fff" }}>Rieker Bottle Works</h1>
          </div>
        </header>
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ background: "#2D8A4E18", border: "2px solid var(--rbw-success)" }}
          >
            <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="var(--rbw-success)" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--rbw-navy)" }}>
            Inquiry Submitted!
          </h2>
          <p className="text-sm mb-6" style={{ color: "var(--rbw-gray-dark)" }}>
            We will review your event details and get back to you with a custom quote within 1-2 business days.
          </p>
          <a
            href="/order"
            className="px-6 py-3 rounded-xl font-semibold transition-all inline-block"
            style={{ background: "var(--rbw-navy)", color: "#fff" }}
          >
            Back to Online Ordering
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--rbw-bg)" }}>
      <header style={{ background: "var(--rbw-navy)" }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <img src="/images/logo-secondary.jpg" alt="Rieker Bottle Works" className="h-10 rounded" />
          <div>
            <h1 className="text-xl font-bold" style={{ color: "#fff" }}>Rieker Bottle Works</h1>
            <p className="text-xs" style={{ color: "var(--rbw-gray)" }}>Events & Catering</p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <a
          href="/order"
          className="inline-flex items-center gap-1.5 text-sm font-medium mb-6 transition-colors"
          style={{ color: "var(--rbw-gray-dark)" }}
        >
          &larr; Back to online ordering
        </a>

        <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--rbw-navy)" }}>
          Request an Event Quote
        </h2>
        <p className="text-sm mb-8" style={{ color: "var(--rbw-gray-dark)" }}>
          Tell us about your event and we will put together a custom quote with volume discounts.
        </p>

        {error && (
          <div
            className="rounded-xl p-4 mb-4 text-sm"
            style={{ background: "#C5303010", border: "1px solid #C5303035", color: "var(--rbw-danger)" }}
          >
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2">
            <div
              className="rounded-2xl p-6"
              style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--rbw-navy)" }}>
                    Event Type
                  </label>
                  <select
                    value={form.event_type}
                    onChange={(e) => updateField("event_type", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl outline-none transition-all text-base"
                    style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
                  >
                    {EVENT_TYPES.map((t) => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--rbw-navy)" }}>
                    Event Date
                  </label>
                  <input
                    type="date"
                    value={form.event_date}
                    onChange={(e) => updateField("event_date", e.target.value)}
                    required
                    className="w-full px-4 py-3 rounded-xl outline-none transition-all text-base"
                    style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--rbw-navy)" }}>
                    Your Name *
                  </label>
                  <input
                    type="text"
                    value={form.customer_name}
                    onChange={(e) => updateField("customer_name", e.target.value)}
                    placeholder="Full name"
                    required
                    className="w-full px-4 py-3 rounded-xl outline-none transition-all text-base"
                    style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--rbw-navy)" }}>
                    Phone *
                  </label>
                  <input
                    type="tel"
                    value={form.customer_phone}
                    onChange={(e) => updateField("customer_phone", e.target.value)}
                    placeholder="(555) 555-5555"
                    required
                    className="w-full px-4 py-3 rounded-xl outline-none transition-all text-base"
                    style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--rbw-navy)" }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={form.customer_email}
                    onChange={(e) => updateField("customer_email", e.target.value)}
                    placeholder="email@example.com"
                    className="w-full px-4 py-3 rounded-xl outline-none transition-all text-base"
                    style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--rbw-navy)" }}>
                    Estimated Guest Count
                  </label>
                  <input
                    type="number"
                    value={form.guest_count}
                    onChange={(e) => updateField("guest_count", parseInt(e.target.value) || 0)}
                    min="1"
                    className="w-full px-4 py-3 rounded-xl outline-none transition-all text-base"
                    style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--rbw-navy)" }}>
                    Event Location
                  </label>
                  <input
                    type="text"
                    value={form.event_location}
                    onChange={(e) => updateField("event_location", e.target.value)}
                    placeholder="Venue name and address"
                    className="w-full px-4 py-3 rounded-xl outline-none transition-all text-base"
                    style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--rbw-navy)" }}>
                    Tell us about your event
                  </label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => updateField("notes", e.target.value)}
                    placeholder="What types of beverages are you looking for? Any special requests?"
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl outline-none transition-all text-base"
                    style={{
                      border: "2px solid var(--rbw-border)",
                      background: "var(--rbw-bg-card)",
                      color: "var(--rbw-navy)",
                      resize: "vertical",
                    }}
                  />
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                className="w-full py-4 rounded-xl font-bold text-lg transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed mt-6"
                style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
              >
                {submitting ? "Submitting..." : "Submit Inquiry"}
              </button>
            </div>
          </div>

          {/* Discount info sidebar */}
          <div>
            <div
              className="rounded-2xl p-5"
              style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}
            >
              <h3 className="text-base font-bold mb-3" style={{ color: "var(--rbw-navy)" }}>
                Volume Discounts
              </h3>
              <p className="text-sm mb-4" style={{ color: "var(--rbw-gray-dark)" }}>
                The more you order, the more you save!
              </p>
              <div className="flex flex-col gap-2">
                {DISCOUNT_INFO.map((tier) => (
                  <div
                    key={tier.min}
                    className="flex justify-between p-3 rounded-lg text-sm"
                    style={{ background: "var(--rbw-bg)" }}
                  >
                    <span className="font-medium" style={{ color: "var(--rbw-navy)" }}>
                      ${tier.min.toLocaleString()}+
                    </span>
                    <span className="font-bold" style={{ color: "var(--rbw-success)" }}>
                      {tier.pct}% off + free delivery
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 rounded-lg" style={{ background: "#F6BE0010", border: "1px solid #F6BE0030" }}>
                <p className="text-xs font-medium" style={{ color: "var(--rbw-gold-dark)" }}>
                  25% deposit required on all event orders. We handle all the heavy lifting - just tell us what you need!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
